import type { ButtonHTMLAttributes, MouseEvent } from 'react';
import { forwardRef, memo, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { ButtonDisabledContextProvider } from './disabled-context.tsx';
import { getVariant } from './helpers.ts';
import { ButtonContent, ButtonWrapper, StyledLoader } from './styled.ts';
import type { ButtonProps } from './types.ts';

type ButtonComponentProps = ButtonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonProps>;

export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonComponentProps>(function Button(
    { children, primary, secondary, critical, ghost, reducedGap, preventDefault, stopPropagation, onClick, dataTestId, ...props },
    ref,
  ) {
    const { colors } = useTheme();
    const variant = getVariant({ primary, secondary, critical, ghost });

    const loaderColor = useMemo(() => {
      if (props.disabled) return colors.grey100_2;
      if (variant === 'primary' || variant === 'critical') return colors.grey00_2;
      if (variant === 'ghost') return colors.black_1;
      return colors.black_1;
    }, [variant, colors, props.disabled]);

    const handleClick = useCallbackRef((e: MouseEvent<HTMLButtonElement>) => {
      if (preventDefault) {
        e.preventDefault();
      }
      if (stopPropagation) {
        e.stopPropagation();
      }
      if (!props.isLoading) {
        onClick?.(e);
      }
    });

    return (
      <ButtonWrapper type='button' variant={variant} onClick={handleClick} ref={ref} data-testid={dataTestId} {...props}>
        {props.isLoading && <StyledLoader content size='16px' fill={loaderColor} />}
        <ButtonContent $reducedGap={reducedGap} $isLoading={props.isLoading}>
          <ButtonDisabledContextProvider disabled={!!props.disabled}>{children}</ButtonDisabledContextProvider>
        </ButtonContent>
      </ButtonWrapper>
    );
  }),
);

export type { ButtonProps } from './types.ts';
